import { useRef, useState } from "react";
import { PitScene } from "@mineoptics/brand/canvas/PitScene";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { useReducedMotion } from "@/lib/motion";

const STAGES = [
  {
    name: "Sites & drillholes",
    title: "Start with the ground.",
    body: "Draw a site boundary on the map, then set its coordinate system and rock properties. Import collars, surveys, geology and samples from CSV. Traces are desurveyed by minimum curvature and drawn in a 3D site view.",
  },
  {
    name: "Drill & blast",
    title: "Design the pattern. Size the fleet.",
    body: "Set bench height, powder factor and spacing ratio, then compare drill options side by side: burden, spacing, holes and metres per year, rigs required and drilling cost per tonne.",
  },
  {
    name: "Load & haul",
    title: "Put the fleet on the road.",
    body: "Record loaders, trucks, haul distance, speeds and cycle times, availability and fuel, with rates filled from a vendor quote. Or build the fleet machine by machine and cost it on the mine plan.",
  },
  {
    name: "Processing",
    title: "Follow the ore to the plant.",
    body: "Crushing stages, screening and conveying, power, water and labour, recorded against the same scenario.",
  },
  {
    name: "Scenario",
    title: "Hold the whole operation in one scenario.",
    body: "Add support services, revenue and lifecycle assumptions. Calculate, change the mine plan or the discount rate and calculate again, then issue the result as a numbered report.",
  },
];

const LAST = STAGES.length - 1;

export function PitSequence() {
  const root = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const progress = useRef(0);
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);

  useGSAP(
    () => {
      if (reduced) {
        progress.current = 1;
        return;
      }

      gsap.from("[data-pit-canvas]", {
        autoAlpha: 0,
        scale: 0.86,
        ease: "none",
        scrollTrigger: { trigger: stageRef.current, start: "top bottom", end: "top top", scrub: true },
      });

      ScrollTrigger.create({
        trigger: stageRef.current,
        start: "top top",
        end: () => `+=${window.innerHeight * 4.2}`,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          progress.current = self.progress;
          gsap.set("[data-pit-bar]", { scaleX: self.progress });
          setActive(Math.min(LAST, Math.floor(self.progress * STAGES.length)));
        },
      });
    },
    { scope: root, dependencies: [reduced] },
  );

  return (
    <section ref={root} id="platform" data-theme="dark" className="relative bg-bg">
      <div className="shell pb-10 pt-20 md:pb-14 md:pt-28">
        <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
          The platform
        </p>
        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <h2 data-split className="title max-w-[13ch]">
            From first collar to cost per tonne.
          </h2>
          <p data-fade className="lede max-w-[40ch] text-mute lg:justify-self-end">
            A site in MineOptics carries its own geometry, rock and data. The cost model is built on top of it, one stage
            at a time.
          </p>
        </div>
      </div>

      {reduced ? (
        <div className="shell pb-20">
          <div className="relative h-[70vh] min-h-[420px]">
            <PitScene progress={progress} animate={false} className="h-full w-full" />
          </div>
          <ol className="mt-12 grid gap-10 md:grid-cols-2 xl:grid-cols-5">
            {STAGES.map((stage, index) => (
              <li key={stage.name}>
                <p className="eyebrow text-accent">
                  0{index + 1} · {stage.name}
                </p>
                <h3 className="mt-3 text-xl font-medium tracking-[-0.02em]">{stage.title}</h3>
                <p className="mt-3 text-mute">{stage.body}</p>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <div ref={stageRef} className="relative h-[100svh] overflow-hidden">
          <div data-pit-canvas className="absolute inset-0">
            <PitScene progress={progress} animate className="h-full w-full" />
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-bg via-bg/70 to-transparent md:hidden" />
          <div className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(70%_80%_at_0%_100%,rgba(9,11,10,0.94)_0%,rgba(9,11,10,0.6)_45%,transparent_72%)] md:block" />

          {/* Stage index */}
          <ol className="absolute right-5 top-28 hidden space-y-3 sm:right-8 md:block lg:right-12">
            {STAGES.map((stage, index) => (
              <li
                key={stage.name}
                className={`eyebrow flex items-center gap-3 transition-colors duration-500 ${
                  index === active ? "text-fg" : index < active ? "text-fg/45" : "text-fg/25"
                }`}
              >
                <span
                  className={`h-px transition-all duration-500 ease-out-expo ${
                    index === active ? "w-8 bg-lime" : "w-3 bg-current"
                  }`}
                />
                0{index + 1} {stage.name}
              </li>
            ))}
          </ol>

          {/* Stage copy */}
          <div className="shell absolute inset-x-0 bottom-0 pb-10 md:pb-14">
            <div className="relative h-[15.5rem] max-w-[34rem] md:h-[15rem]">
              {STAGES.map((stage, index) => (
                <div
                  key={stage.name}
                  aria-hidden={index !== active}
                  className={`absolute inset-x-0 bottom-0 transition-[opacity,transform] duration-700 ease-out-expo ${
                    index === active
                      ? "translate-y-0 opacity-100"
                      : index < active
                        ? "-translate-y-6 opacity-0"
                        : "translate-y-6 opacity-0"
                  }`}
                >
                  <p className="eyebrow flex items-center gap-3 text-accent">
                    <span className="tabular">
                      0{index + 1} / 0{STAGES.length}
                    </span>
                    <span className="text-mute md:hidden">{stage.name}</span>
                  </p>
                  <h3 className="subtitle mt-4 uppercase">{stage.title}</h3>
                  <p className="mt-4 text-fg/75">{stage.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 h-px w-full bg-fg/15">
              <div data-pit-bar className="h-px w-full origin-left scale-x-0 bg-lime" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
